import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Box, TextField, InputAdornment, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Button from '../../components/CustomButtons/Button';
import Tile from '../../components/Tile/Tile';
import { useNotifications } from 'modules/notification';
import { UPDATE_COMPANY_INFO } from '../CompanyProfile/api';
import { SET_ACCOUNT } from '../../redux/types';

const CHECK_MAGIC_EMAIL_AVAILABILITY = gql`
  query CheckMagicEmailAvailability($email: String!) {
    checkMagicEmailAvailability(email: $email) {
      available
      error
    }
  }
`;

const MAGIC_EMAIL_DOMAIN = '@magic.trnsact.com';

const useStyles = makeStyles({
  title: {
    margin: '0 0 20px',
    fontWeight: 'bold',
  },
  label: {
    fontWeight: '500',
  },
});

export const MagicEmailInboxCard = () => {
  const classes = useStyles();
  const account = useSelector(state => state.account);
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();

  const existingEmail = account.magicEmailAddress || '';
  const isAlreadySet = Boolean(existingEmail);

  const [dealerName, setDealerName] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);

  const fullEmail = dealerName ? `${dealerName}${MAGIC_EMAIL_DOMAIN}` : '';

  const [updateAccount, { loading: saving }] = useMutation(UPDATE_COMPANY_INFO, {
    context: { authRequired: true },
    onCompleted() {
      dispatch({ type: SET_ACCOUNT, payload: { ...account, magicEmailAddress: fullEmail } });
      showNotification('Magic email address saved');
    },
    onError(err) {
      showNotification(err.message || 'Error saving email address', { type: 'error' });
    },
  });

  const [checkAvailability, { loading: checking }] = useLazyQuery(CHECK_MAGIC_EMAIL_AVAILABILITY, {
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      const result = data?.checkMagicEmailAvailability;
      if (result?.available) {
        updateAccount({
          variables: {
            dynamicsId: account.dynamicsAccountId,
            fieldsObj: { magicEmailAddress: fullEmail },
          },
        });
      } else {
        setEmailTaken(true);
      }
    },
    onError() {
      showNotification('Error checking availability', { type: 'error' });
    },
  });

  const handleSave = () => {
    if (!dealerName.trim()) return;
    setEmailTaken(false);
    checkAvailability({ variables: { email: fullEmail } });
  };

  const isBusy = checking || saving;

  return (
    <Tile>
      <h3 className={classes.title}>Magic Email Inbox</h3>
      {isAlreadySet ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <h4 className={classes.label}>Magic email address</h4>
          <p>{existingEmail}</p>
          <Typography variant="caption" color="text.secondary">
            This address cannot be changed once set.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <p>Set a unique magic email inbox address for this dealer. Once saved, it cannot be changed.</p>
          <TextField
            label="Dealer name"
            value={dealerName}
            onChange={e => {
              setDealerName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''));
              setEmailTaken(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {MAGIC_EMAIL_DOMAIN}
                  </Typography>
                </InputAdornment>
              ),
            }}
            helperText={emailTaken ? 'This email address is already taken.' : fullEmail || ' '}
            FormHelperTextProps={{
              sx: { color: emailTaken ? 'error.main' : 'text.secondary' },
            }}
            size="small"
            sx={{ maxWidth: 380 }}
          />
          <Box>
            <Button color="primary" onClick={handleSave} disabled={!dealerName.trim() || isBusy}>
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Tile>
  );
};
